import { Link } from 'react-router';
import { LegalPage } from '../../components/LegalPage';
import { CONTACT_EMAIL, GOVERNING_COURTS, OPERATOR, OPERATOR_DESCRIPTION } from '../../lib/legal';

const mail = <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;

export function Terms() {
  return (
    <LegalPage
      title="Terms of Service"
      summary={
        <ul className="list-disc space-y-1 pl-5">
          <li>You need to be 13 or older, with a parent’s permission if you’re under 18.</li>
          <li>Play fair and be kind: no cheating, harassment, illegal content or abuse.</li>
          <li>What you create stays yours; you let us show it on MOGZO.</li>
          <li>MOGZO is in early access and free, and things may change or break.</li>
        </ul>
      }
    >
      <h2>1. About these terms</h2>
      <p>
        These terms are an agreement between you and {OPERATOR_DESCRIPTION} (“{OPERATOR}”, “we”,
        “us”), which runs MOGZO at mogzo.com. By creating an account or using MOGZO, you agree to
        these terms and to our <Link to="/privacy">Privacy Policy</Link>. If you don’t agree, please
        don’t use MOGZO.
      </p>

      <h2>2. Who can use MOGZO</h2>
      <ul>
        <li>You must be at least 13 years old to create an account.</li>
        <li>
          If you are under 18 (or under the age of adulthood where you live), a parent or guardian
          must agree to these terms for you and supervise your use of MOGZO.
        </li>
        <li>You can’t use MOGZO if we have suspended you, or if the law doesn’t allow you to.</li>
      </ul>

      <h2>3. Your account</h2>
      <ul>
        <li>Give accurate information, including your month and year of birth.</li>
        <li>
          Keep your password and two-factor codes secret. You are responsible for what happens under
          your account. Tell us at {mail} if you think someone else has used it.
        </li>
        <li>One person per account. Don’t sell, share or transfer accounts.</li>
        <li>Pick a display name that doesn’t impersonate anyone or offend others.</li>
      </ul>

      <h2>4. Rules for using MOGZO</h2>
      <p>You agree not to:</p>
      <ul>
        <li>harass, threaten, bully or hurt anyone, or promote hatred or violence;</li>
        <li>
          post anything illegal, sexual content involving minors, or content that infringes someone
          else’s rights;
        </li>
        <li>cheat, exploit bugs or use bots to gain an unfair advantage;</li>
        <li>
          hack, overload, scrape or interfere with MOGZO, or get around our security or limits;
        </li>
        <li>upload malware, or trick people into giving away their data;</li>
        <li>spam, or use MOGZO to advertise without our permission;</li>
        <li>pretend to be someone else, including MOGZO staff.</li>
      </ul>
      <p>
        We may remove content or limit, suspend or close accounts that break these rules. Where we
        reasonably can, we will tell you why.
      </p>

      <h2>5. What you create</h2>
      <p>
        Content you create on MOGZO (such as posts, comments, scores, levels or game data) stays
        yours. You give us a worldwide, non-exclusive, royalty-free licence to host, store, copy,
        display and share it on MOGZO, so the service can work. This licence ends when you delete
        the content or your account, except where others have already shared it or we must keep it
        by law.
      </p>
      <p>
        Only post content you have the right to share. If you believe something on MOGZO infringes
        your rights or breaks these terms, report it to {mail}.
      </p>

      <h2>6. Games by other developers</h2>
      <p>
        Many games on MOGZO will be made by independent developers. We review games before they are
        published, but each developer is responsible for their own game. Developers who publish on
        MOGZO will also agree to separate developer terms.
      </p>

      <h2>7. MOGZO’s own content</h2>
      <p>
        The MOGZO name, logo, mascot, design and software belong to {OPERATOR}. You may not copy or
        use them without our written permission, except to refer to MOGZO in a normal way.
      </p>

      <h2>8. Early access and changes</h2>
      <p>
        MOGZO is free and in early access. Features may change, be added or be removed, and the
        service may sometimes be unavailable. We may update these terms; if a change is significant,
        we will tell you before it takes effect. If you keep using MOGZO after that, the new terms
        apply.
      </p>

      <h2>9. Ending your account</h2>
      <p>
        You can stop using MOGZO at any time and ask us to delete your account at {mail}. We may
        suspend or close your account if you break these terms, if the law requires it, or if we
        shut MOGZO down (in which case we will give reasonable notice).
      </p>

      <h2>10. No warranties</h2>
      <p>
        MOGZO is provided “as is” and “as available”. To the extent the law allows, we make no
        promises that it will be uninterrupted, error-free or fit for a particular purpose.
      </p>

      <h2>11. Limits on our liability</h2>
      <p>
        To the extent the law allows, {OPERATOR} is not liable for indirect or consequential losses,
        lost data, lost profits, or anything caused by other users or third-party games. Our total
        liability to you for any claim is limited to ₹1,000 (one thousand Indian rupees). Nothing in
        these terms limits liability that the law doesn’t allow us to limit.
      </p>

      <h2>12. Law and disputes</h2>
      <p>
        These terms are governed by the laws of India. If a dispute comes up, please contact us
        first at {mail}; most issues can be sorted out quickly. If not, the courts of{' '}
        {GOVERNING_COURTS} will have exclusive jurisdiction. If you live outside India, you keep any
        rights your local consumer laws give you.
      </p>

      <h2>13. Contact and grievances</h2>
      <p>
        Questions, reports and complaints go to the Grievance Officer, {OPERATOR}, at {mail}. We
        acknowledge them within 24 hours and aim to resolve them within 15 days.
      </p>
    </LegalPage>
  );
}
